import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

import { ThemeProvider } from '@mui/material/styles';

import ErrorPage from './components/common/error_page/ErrorPage';
import Snackbar from './components/common/snackbar/Snackbar';
import AIModelDetailPage from './features/ai_model/page/AIModelDetailPage';
import AIModelPage from './features/ai_model/page/AIModelPage';
import StudyAnnotationPage from './features/annotation/page/StudyAnnotationPage';
import AnnotationLabelPage from './features/annotation_label/page/AnnotationLabelPage';
import { SignInPage } from './features/auth';
import AutoSignInPage from './features/auth/pages/AutoSignInPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import JobPage from './features/job/pages/JobPage';
import PatientDetailsPage from './features/patient/pages/PatientDetailsPage';
import UserProfilePage from './features/profile/pages/UserProfilePage';
import QuestionnairePage from './features/questionnaire/pages/QuestionnairePage';
import { QuestionnaireTemplateBuilderPage } from './features/questionnaire_template/pages/QuestionnaireTemplateBuilderPage';
import SiteHomePage from './features/site/pages/SiteHomePage';
import SiteSettingsPage from './features/site/pages/SiteSettingsPage';
import AIReportPage from './features/study/pages/AIReportPage';
import StudyAssignmentsPage from './features/study_assignment/page/StudyAssignmentPage';
import UsersPage from './features/user/pages/UsersPage';
import ProtectedRoute from './routes/ProtectedRoute';
import { RootRedirect } from './routes/RootRedirect';
import theme from './theme';

import './App.css';
import './index.css';

function App() {
	return (
		<ThemeProvider theme={theme}>
			<Router>
				<div className='App'>
					<Routes>
						<Route path='/' element={<RootRedirect />} />
						<Route path='/signin' element={<SignInPage />} />
						<Route path='/login' element={<AutoSignInPage />} />
						<Route
							path='/dashboard'
							element={
								<ProtectedRoute
									allowedGlobalRoles={[
										'super_admin',
										'general',
									]}
								>
									<DashboardPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path='/user-profile'
							element={
								<ProtectedRoute
									allowedGlobalRoles={[
										'super_admin',
										'general',
									]}
								>
									<UserProfilePage />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/users'
							element={
								<ProtectedRoute
									allowedGlobalRoles={['super_admin']}
								>
									<UsersPage />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug'
							element={
								<ProtectedRoute
									allowedSiteRoles={[
										'super_admin',
										'admin',
										'annotator',
										'data_collector',
									]}
								>
									<SiteHomePage />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug/study-assignments'
							element={
								<ProtectedRoute
									allowedSiteRoles={[
										'super_admin',
										'admin',
										'annotator',
									]}
								>
									<StudyAssignmentsPage />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug/questionnaire-templates'
							element={
								<ProtectedRoute
									allowedSiteRoles={['super_admin', 'admin']}
								>
									<SiteHomePage tableType='questionnaire_template' />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug/questionnaire-template-builder'
							element={
								<ProtectedRoute
									allowedSiteRoles={['super_admin', 'admin']}
								>
									<QuestionnaireTemplateBuilderPage key='create-mode' />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug/questionnaire-template-builder/:questionnaireTemplateId'
							element={
								<ProtectedRoute
									allowedSiteRoles={['super_admin', 'admin']}
								>
									<QuestionnaireTemplateBuilderPage key='edit-mode' />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug/settings'
							element={
								<ProtectedRoute
									allowedSiteRoles={['super_admin', 'admin']}
								>
									<SiteSettingsPage />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug/patient/:patientId'
							element={
								<ProtectedRoute
									allowedSiteRoles={[
										'super_admin',
										'admin',
										'annotator',
										'data_collector',
									]}
								>
									<PatientDetailsPage />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug/patient/:patientId/study/:studyId'
							element={
								<ProtectedRoute
									allowedSiteRoles={[
										'super_admin',
										'admin',
										'annotator',
										'data_collector',
									]}
								>
									<QuestionnairePage />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug/patient/:patientId/study/:studyId/study-annotation'
							element={
								<ProtectedRoute
									allowedSiteRoles={[
										'super_admin',
										'admin',
										'annotator',
									]}
								>
									<StudyAnnotationPage />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug/patient/:patientId/study/:studyId/ai-report'
							element={
								<ProtectedRoute
									allowedSiteRoles={[
										'super_admin',
										'admin',
										'annotator',
										'data_collector',
									]}
								>
									<AIReportPage />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug/site-download'
							element={
								<ProtectedRoute
									allowedSiteRoles={['super_admin', 'admin']}
								>
									<JobPage />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/site/:siteSlug/annotation-label'
							element={
								<ProtectedRoute
									allowedSiteRoles={['super_admin', 'admin']}
								>
									<AnnotationLabelPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path='/site/:siteSlug/ai-models'
							element={
								<ProtectedRoute
									allowedSiteRoles={['super_admin', 'admin']}
								>
									<AIModelPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path='/site/:siteSlug/ai-models/:aiModelId'
							element={
								<ProtectedRoute
									allowedSiteRoles={['super_admin', 'admin']}
								>
									<AIModelDetailPage />
								</ProtectedRoute>
							}
						/>

						<Route
							path='/unauthorized'
							element={
								<ErrorPage
									title='Unauthorized'
									message='You do not have permission to access this page.'
								/>
							}
						/>

						<Route
							path='*'
							element={
								<ErrorPage
									title='Page Not Found'
									message="The page you're looking for doesn't exist."
								/>
							}
						/>
					</Routes>
					<Snackbar />
				</div>
			</Router>
		</ThemeProvider>
	);
}

export default App;
